import React, {useMemo} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import Dependencies from "../../../../../utilities/Dependencies";
import Environment from "../../../../../services/interfaces/Environment";
import PageActions from "../../../../base/PageActions";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";

interface HistoryPageProps {
    dependencies: Dependencies,
    env: Environment
}

const HistoryPage = (props: HistoryPageProps) => {

    const page: PageActions = useMemo(() => new PageActions({
        dependencies: props.dependencies,
        env: props.env,
        title: `Casarok Geography - Chase the Fox`,
        description: "The world of Casarok has a history that follows a similar timeline to that of Earth, though interrupted by the Great Cataclysm!",
        keywords: ["casarok", "casarok history", "fantasy timeline", "fantasy world history", "great cataclysm"],
        imgSrc: `chase/world/geography/post-cataclysm.png`,
        url: `world/history`
    }), [props]);

    const switchVisible = (): void => {
        let initiallyVisibleTarget: HTMLElement = document.getElementById('visible-element')!;
        let initiallyHiddenTarget: HTMLElement = document.getElementById('hidden-element')!;

        if (initiallyVisibleTarget.className.includes('visible')) {
            initiallyVisibleTarget.classList.add('switch-hidden');
            initiallyVisibleTarget.classList.remove('switch-visible');
            initiallyHiddenTarget.classList.add('switch-visible');
            initiallyHiddenTarget.classList.remove('switch-hidden');
        } else {
            initiallyHiddenTarget.classList.add('switch-hidden');
            initiallyHiddenTarget.classList.remove('switch-visible');
            initiallyVisibleTarget.classList.add('switch-visible');
            initiallyVisibleTarget.classList.remove('switch-hidden');
        }
    }

    return page.generateMetaContent(
        <>
            <ContentBox header="The History of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <div className="world-content center">
                        <button className="world-button" onClick={() => switchVisible()}>
                            Show the info for the other era
                        </button>
                    </div>
                    <p>
                        Casarok has a long and storied history. Modern historians have categorized the history of the
                        world into three eras made up of various ages.
                    </p>
                    <p>
                        The <strong>Creation Era</strong> represents the earliest existence of the planet and its
                        inhabitants prior to around 10,000 BCE when the first intelligent life was known to exist.
                        During this era the universe, planetary system, and Casarok itself was created by the will of
                        the <NavLink className="world-link" to="/world/pantheon">gods and goddesses</NavLink>. They also
                        placed the primordials throughout the world in this era. Fossils from this time are extremely
                        sought after as they provide the only palpable knowledge of this time period.
                    </p>
                    <p>
                        The <strong>Pre-Cataclysmic Era</strong> <em>(about 10,000 BCE - 1,000 CE)</em> is the earliest
                        known time of intelligent life on the planet. All of the native modern
                        <NavLink className="world-link" to="/world/bestiary">races of Casarok</NavLink> appeared on the
                        planet during time in one manner or another. The pantheon of Casarok was particularly active
                        during this time as they all were slain, fell out of favor, or no longer took an active role
                        in the events of the world after the end of this era. The majority of the history of this era
                        is lost.
                    </p>
                    <p>
                        The <strong>Post-Cataclysmic Era</strong> <em>(1000 CE and onward)</em> is the most modern era
                        in which the world was divided into six continents. Aside from one incident early in this age,
                        the world was devoid of the influence of the gods and goddesses of Casarok. All advancement was
                        left to the intelligent races of the world. A strict record of history has been kept ever since
                        the end of the War of the Gods.
                    </p>
                    <h3 className="headline">The Ages</h3>
                    <ul id="visible-element" className="spaced switch-visible">
                        <li>
                            <strong>Draconic Age</strong>: <em>(about 10,000 - 6,000 BCE)</em> Primordial dragons
                                    evolved into the intelligent species of 
                                    the <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race.
                        </li>
                        <li>
                            <strong>Iron Age</strong>: <em>(6,000 - 3,000 BCE)</em> The emergence of the
                                   the <NavLink className="world-link" to="/world/bestiary/aspect">Aspect</NavLink> and
                                   the <NavLink className="world-link" to="/world/bestiary/anthro">Anthro</NavLink> races
                                   occurred during this age. Bronze and iron tools were developed and used to
                                   enslave the weaker Anthro race.
                        </li>
                        <li>
                            <strong>Rebellion Age</strong>: <em>(3,000 - 1,000 BCE)</em> The Anthro species, having
                                    grown in number from the previous age, won their independence from the stronger races.
                                    Most Anthros became free during this time.
                        </li>
                        <li>
                            <strong>Magical Age</strong>: <em>(1,000 - 200 BCE)</em> The Anthro race made great
                                    advancements in the use of magic.
                        </li>
                        <li>
                            <strong>Empirical Age</strong>: <em>(200 BCE - 500 CE)</em> The world was divided into many
                                    large and powerful empires. (Similar to the Roman era of Earth history.)
                        </li>
                        <li>
                            <strong>Crusade Age</strong>: <em>(500 - 600 CE)</em> A large culture spread resulting from
                                    the fall of the great empires. The Common language became popularized during this
                                    age due to this distribution of influence and culture.
                        </li>
                        <li>
                            <strong>Feudal Age</strong>: <em>(600 - 900 CE)</em> Casarok was once again controlled by
                                    massive entities, this time in the form of kingdoms. However, they practiced
                                    diplomacy rather than waging border wars. (Similar to the same age of human
                                    history.)
                        </li>
                    </ul>
                    <ul className="spaced">
                        <li>
                            <strong>Cataclysmic Age</strong>: <em>(900 - 1000 CE)</em> The War of the Gods occurred in
                                    which the gods and goddesses fought over the coming of the Endtimes, the destruction
                                    of all life on Casarok. A great earthquake split the singular landmass into
                                    continents. Those mortals that survived were left to rebuild their crumbled
                                    civilization. The Aspect species lost their magical abilities by the end of this
                                    era.
                        </li>
                    </ul>
                    <ul id="hidden-element" className="spaced switch-hidden">
                        <li>
                            <strong>Recovery Age</strong>: <em>(1000 - 1300 CE)</em> The survivors of the cataclysm
                                    began to rebuild. Magical knowledge increased dramatically due to influence from
                                    the god of magic, who was quickly banished from Casarok by the god of knowledge.
                        </li>
                        <li>
                            <strong>Enlightened Age</strong>: <em>(1300 - 1500 CE)</em> Technology flourished and many
                                    inventions were conceived. The two key advancements being steam and clockwork
                                    machinery. The people of Casarok became enraptured by steampunk culture.
                        </li>
                        <li>
                            <strong>Expansion Age</strong>: <em>(1500 - 1700 CE)</em> Nations formed and began to
                                    grow into their more modern boundaries. Thorough exploration and mapping of the new
                                    continents occurred. (This is similar to the exploration and colonial portions of
                                    Earth history.)
                        </li>
                        <li>
                            <strong>Industrial Age</strong>: <em>(1700 - 1800 CE)</em> Steam and clockwork technology
                                    gave way to fossil fuels and electricity. Many factories were formed. (Mirrors
                                    the same age of human history.)
                        </li>
                        <li>
                            <strong>Machination Age</strong>: <em>(1800 BCE - 1900 CE)</em> More advances in technology
                                    occurred. Mage-tech, the union of magic and machines, was invented. Mechanized
                                    weapons became common and the world became engulfed in war. (Similar to the time of
                                    the world wars in human history.)
                        </li>
                        <li>
                            <strong>Modern Age</strong>: <em>(1900 - 2100 CE)</em> Magical and technological advances
                                    continued and a tentative peace was kept through the
                                    world. <NavLink className="world-link" to="/characters/chase">Chase</NavLink> and
                                    his friends live during this time. (Similar to modern times on Earth but with more
                                    advanced technology.)
                        </li>
                        <li>
                            <strong>Space Age</strong>: <em>(2100 - 2300 CE)</em> The people of Casarok begin exploring
                                    their planetary system and discovering various alien races. Personal space travel
                                    becomes inexpensive, and colonies are formed on uninhabited planets.
                        </li>
                        <li>
                            <strong>Lightspeed Age</strong>: <em>(2300 CE and beyond)</em> Light-speed travel becomes
                                    possible. Casarok nears the pinnacle of most technological advances and must frontier
                                    to the depths of space to push their limits further.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default HistoryPage;